import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  Calendar,
  Filter,
  ChevronDown,
  FileText,
  User,
  Store,
  Search,
  CreditCard
} from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { UserRole } from '../../types/auth';
import { OrderDetailModal } from './GatewayOrderDetailModal';
import { toast } from 'react-toastify';
import { useAppSelector, useAppDispatch } from '../../store/hooks/reduxHooks';
import {
  fetchOrdersThunk,
  fetchOrderDetailThunk,
  updateOrderThunk,
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
  setFilters,
  resetFilters,
  clearSelectedOrder
} from '../../features/gatewayOrders/gatewayOrderSlice';
import { Order, OrderStatus, OrderFilters, OrderDetail } from '../../services/gatewayOrder';
import { validateCustomerSearchInput } from "../../utils/searchValidator";

// Document type


const ManageOrder: React.FC = () => {
  // Get current user role to determine permissions
  const { user } = useAppSelector(state => state.auth);
  const currentUserRole = user?.role || UserRole.CLEVERPE_USER;
  const { token } = useAppSelector(state => state.auth);

  // Check if user can manage orders (needs both the right role AND the specific permission)
  const canManageOrders = useMemo(() => {
    // Check if user has the specific manage-order permission
    const hasManageOrdersPermission = user?.dashPermissions?.includes('manage-gateway-order') || false;

    // Only check for the permission, regardless of user role
    return hasManageOrdersPermission;
  }, [user?.dashPermissions]);
  const dispatch = useAppDispatch();

  const {
    orders,
    selectedOrder,
    loading,
    page,
    pageSize,
    total,
    sortBy,
    sortOrder,
    searchQuery,
    filters,
    hasFetched
  } = useAppSelector(state => state.gatewayOrders);


  // Filter state

  const [showFilters, setShowFilters] = useState(false);

  // Modal state
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Available lenders and merchants for filter dropdowns
  const [availableLenders, setAvailableLenders] = useState<string[]>([]);
  const [availableMerchants, setAvailableMerchants] = useState<{ id: string, name: string }[]>([]);
  const prevRef = useRef({
    page,
    pageSize,
    sortBy,
    sortOrder,
    searchQuery,
    filters
  });
  const [localSearch, setLocalSearch] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchValid, setIsSearchValid] = useState(false);
  const handleSearchInputChange = (value: string) => {
    setLocalSearch(value);

    // ✅ When input is cleared
    if (!value.trim()) {
      setSearchError(null);
      setIsSearchValid(true); // allow reset

      // 🔁 Reset table immediately
      dispatch(setPage(1));
      dispatch(setSearchQuery("")); // empty search
      return;
    }

    // Normal validation
    const result = validateCustomerSearchInput(value);
    setIsSearchValid(result.isValid);
    setSearchError(result.message || null);
  };


  const handleSearchSubmit = () => {
    // If empty, already handled in change
    if (!localSearch.trim()) return;

    if (!isSearchValid) return;

    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch));
  };

  const handleResetSearch = () => {
    setLocalSearch("");    // clears input
    setSearchError(null);  // clears error
    setIsSearchValid(false); // disables search button
  };




  // Initial fetch - only if orders not already loaded
  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder ||
      prev.filters.status !== filters.status ||
      prev.filters.lender !== filters.lender ||
      prev.filters.dateRange?.from !== filters.dateRange?.from ||
      prev.filters.dateRange?.to !== filters.dateRange?.to;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchOrdersThunk());

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        filters: {
          ...filters,
          dateRange: { ...filters.dateRange },
        },
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    filters.lender,
    filters.status,
    filters.dateRange?.from,
    filters.dateRange?.to,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasFetched,
    dispatch,
  ]);

  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;


    dispatch(fetchOrdersThunk());

  }, [token, hasFetched, dispatch]);


  const handleRefresh = () => {
    dispatch(setPage(1));
    dispatch(fetchOrdersThunk());
  };


  // Fetch filter options (lenders, merchants)
  const fetchFilterOptions = async () => {
    try {
      // Use hardcoded lenders instead of fetching
      setAvailableLenders(['Zype', 'Cashe', 'General']);

      // Default to empty merchants array
      setAvailableMerchants([]);


    } catch (error) {
      console.error('Error in fetchFilterOptions:', error);
    }
  };


  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (value: string | number) => {
    const date = new Date(Number(value));
    return date.toLocaleDateString('en-IN', {
      year: '2-digit',
      month: 'short',
      day: 'numeric'
    });
  };


  // Render status badge with appropriate color
  const renderStatusBadge = (status: OrderStatus) => {
    let bgColor = '';
    let textColor = '';

    switch (status) {
      case 'In Process':
        bgColor = 'bg-cyan-100';
        textColor = 'text-cyan-800';
        break;
      case 'Expired':
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        break;
      case 'Product Verified':
        bgColor = 'bg-teal-100';
        textColor = 'text-teal-800';
        break;
      case 'QR Activated':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'Verification Pending':
        bgColor = 'bg-indigo-100';
        textColor = 'text-indigo-800';
        break;
      case 'Failed':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'Approved':
        bgColor = 'bg-orange-100';
        textColor = 'text-orange-800';
        break;
      case 'Completed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Cancelled':
        bgColor = 'bg-red-100';
        textColor = 'text-red-900';
        break;
      default:
        bgColor = 'bg-transparent';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor} capitalize`}>
        {status}
      </span>
    );
  };

  // Render lender badge with appropriate color
  const renderLenderBadge = (lender: string) => {
    let bgColor = '';
    let textColor = '';

    switch (lender.toLowerCase()) {
      case 'cashe':
        bgColor = 'bg-yellow-500';
        textColor = 'text-white';
        break;
      case 'zype':
        bgColor = 'bg-black';
        textColor = 'text-white';
        break;
      case 'general':
        bgColor = 'bg-blue-500';
        textColor = 'text-white';
        break;
      default:
        bgColor = 'bg-gray-200';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {lender}
      </span>
    );
  };

  // Table columns
  const columns: ColumnDef<Order, any>[] = [
    {
      id: 'srNo',
      header: 'Sr. No',
      size: 60,
      cell: ({ row }) => {
        // Calculate serial number based on pagination
        const rowIndex = row.index;
        const serialNumber = (page - 1) * pageSize + rowIndex + 1;
        return <span className="text-xs">{serialNumber}</span>;
      }
    },
    {
      id: 'orderId',
      header: 'Order ID',
      accessorKey: 'orderId',
      size: 90,
      cell: ({ row }) => <span className="text-xs font-medium">{row.original.orderId}</span>
    },
    {
      id: 'utrNumber',
      header: 'UTR Number',
      accessorKey: 'utr',
      size: 100,
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.utr || 'N/A'}</span>
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 80,
      cell: ({ row }) => renderStatusBadge(row.original.status)
    },
    {
      id: 'customer',
      header: 'Customer Info',
      accessorKey: 'customerId',
      size: 120,
      cell: ({ row }) => (
        <div className="py-0.5">
          <div className="flex items-center text-xs font-medium">
            <User size={10} className="mr-1 text-gray-500" />
            <span className="truncate max-w-[90px]" title={row.original.settlement?.customerName || 'N/A'}>
              {row.original.settlement?.customerName || 'N/A'}
            </span>
          </div>
          <div className="text-xs text-gray-500">{row.original.settlement?.customerMobileNo || 'N/A'}</div>
        </div>
      )
    },
    {
      id: 'merchant',
      header: 'Merchant Info',
      accessorKey: 'merchantId',
      size: 120,
      cell: ({ row }) => (
        <div className="py-0.5">
          <div className="flex items-center text-xs font-medium">
            <Store size={10} className="mr-1 text-gray-500" />
            <span className="truncate max-w-[90px]" title={row.original.settlement?.merchantName || 'N/A'}>
              {row.original.settlement?.merchantName || 'N/A'}
            </span>
          </div>
          <div className="text-xs text-gray-500">{row.original.settlement?.merchantMobileNo || 'N/A'}</div>
        </div>
      )
    },
    {
      id: 'documents',
      header: 'Documents',
      size: 100,
      cell: ({ row }) => {
        const hasProductDoc = row.original.docProduct;
        const hasReceiptDoc = row.original.docReceipt;

        if (!hasProductDoc && !hasReceiptDoc) {
          return <span className="text-xs text-gray-500">No Document</span>;
        }

        return (
          <div className="flex flex-col py-0.5">
            {hasProductDoc && (
              <div className="flex items-center text-xs">
                <FileText size={10} className="mr-1 text-gray-500" />
                <span>Product Document</span>
              </div>
            )}
            {hasReceiptDoc && (
              <div className="flex items-center text-xs">
                <FileText size={10} className="mr-1 text-gray-500" />
                <span>Receipt Document</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      id: 'amount',
      header: 'Amount',
      accessorKey: 'amount',
      size: 80,
      cell: ({ row }) => (
        <span className="text-xs font-medium text-gray-900">
          {row.original.amount
            ? formatCurrency(row.original.amount)
            : 'N/A'}
        </span>
      )
    },
    {
      id: 'orderCreatedAt',
      header: 'Order Date',
      accessorKey: 'orderCreatedAt',
      size: 80,
      cell: ({ row }) => (
        <div className="flex items-start text-xs py-0.5">
          <Calendar size={10} className="text-gray-500 mr-1 mt-0.5" />
          <span>
            {row.original.orderCreatedAt
              ? formatDate(row.original.orderCreatedAt)
              : '-'}
          </span>
        </div>
      )
    },
    {
      id: 'lender',
      header: 'Lender',
      accessorKey: 'lender',
      size: 90,
      cell: ({ row }) => (
        <div className="flex items-center text-xs">
          {renderLenderBadge(row.original.lender)}
        </div>
      )
    }
  ];

  // Add styles to reduce table cell padding
  useEffect(() => {
    // Create style element
    const style = document.createElement('style');
    style.innerHTML = `
      .orders-table td {
        padding: 4px 6px !important;
      }
      .orders-table th {
        padding: 4px 6px !important;
        font-size: 0.7rem !important;
      }
    `;
    // Add style to document
    document.head.appendChild(style);

    // Clean up on unmount
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle row click
  const handleRowClick = async (order: Order) => {
    const result = await dispatch(fetchOrderDetailThunk(order.orderId));

    if (fetchOrderDetailThunk.fulfilled.match(result)) {
      setDetailsOpen(true);
    } else {
      toast.error('Failed to fetch order details');
    }
  };

  // Handle closing details dialog
  const handleCloseDetails = () => {
    setDetailsOpen(false);          // local UI state
    dispatch(clearSelectedOrder()); // redux state
  };


  // Handle filter changes
  const handleFilterChange = (key: keyof OrderFilters, value: any) => {
    dispatch(
      setFilters({
        ...filters,
        [key]:
          key === 'dateRange'
            ? { ...filters.dateRange, ...value }
            : value,
      })
    );

    dispatch(setPage(1));
  };




  // Handle filter reset
  const handleResetFilters = () => {
    dispatch(resetFilters());
    dispatch(setPage(1));
  };



  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(prev => !prev);
  };

  // Handle order update
  const handleUpdateOrder = async (updatedOrder: OrderDetail) => {
    const payload: any = {
      status: updatedOrder.status,
      utr: updatedOrder.utr,
      utrReason: updatedOrder.utrReason,
    };

    if (updatedOrder.transaction?.transactionId) {
      payload.transaction = {
        transactionId: updatedOrder.transaction.transactionId
      };
    }

    const result = await dispatch(
      updateOrderThunk({
        orderId: updatedOrder.orderId,
        payload
      })
    );

    if (updateOrderThunk.fulfilled.match(result)) {
      toast.success('Order updated successfully');
      dispatch(fetchOrdersThunk()); // refresh table
    } else {
      toast.error('Failed to update order');
    }
  };


  return (
    <div className="w-full no-scrollbar">
      {/* Filters section */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <Button
            variant="outline"
            onClick={toggleFilters}
            className="flex items-center"
            size="sm"
          >
            <Filter size={10} className="mr-1" />
            Filters
            <ChevronDown size={10} className="ml-1" />
          </Button>
        </div>

        {/* Filter controls */}
        {showFilters && (
          <div className="p-2 mb-2 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="grid grid-cols-12 gap-2">
              {/* Status filter */}
              <div className="col-span-3">
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-1 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All Statuses</option>
                  {/* <option value="In Process">In Process</option> */}
                  <option value="Expired">Expired</option>
                  {/* <option value="Product Verified">Product Verified</option> */}
                  <option value="QR Activated">QR Activated</option>
                  <option value="Verification Pending">Verification Pending</option>
                  <option value="Failed">Failed</option>
                  <option value="Approved">Approved</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              {/* Lender filter */}
              <div className="col-span-3">
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  Lender
                </label>
                <select
                  value={filters.lender}
                  onChange={(e) => handleFilterChange('lender', e.target.value)}
                  className="w-full px-1 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">All Lenders</option>
                  {['cashe', 'zype'].map((l) => (
                    <option key={l} value={l}>
                      {l === 'cashe' ? 'Cashe' : l === 'zype' ? 'Zype' : l}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date range filter */}
              <div className="col-span-3">
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.dateRange.from || ''}
                  onChange={(e) => handleFilterChange('dateRange', { from: e.target.value || null })}
                  className="w-full px-1 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="col-span-3">
                <label className="block mb-1 text-xs font-medium text-gray-700">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.dateRange.to || ''}
                  onChange={(e) => handleFilterChange('dateRange', { to: e.target.value || null })}
                  className="w-full px-1 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Filter actions */}
              <div className="flex items-end justify-end col-span-12 mt-2 mb-1">
                <Button
                  variant="outline"
                  onClick={handleResetFilters}
                  size="sm"
                  className="mr-1 text-xs py-0.5 h-6 px-2"
                >
                  Reset
                </Button>
                <Button
                  onClick={toggleFilters}
                  size="sm"
                  className="text-xs py-0.5 h-6 px-2"
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={orders}
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(newpage) => dispatch(setPage(newpage))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}

          searchValue={localSearch}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          isSearchDisabled={!isSearchValid}
          searchError={searchError}
          onResetSearch={handleResetSearch} // ✅ new
          onRefresh={handleRefresh}
          enableRowClick
          onRowClick={handleRowClick}
        />

      </div>

      {/* Order Details Dialog */}
      {selectedOrder && (
        <OrderDetailModal
          isOpen={detailsOpen}
          onClose={() => {
            setDetailsOpen(false);
            dispatch(clearSelectedOrder());
          }}
          order={selectedOrder}
          onUpdateOrder={canManageOrders ? handleUpdateOrder : undefined}
          canEdit={canManageOrders}
        />
      )}

    </div>
  );
};

export default ManageOrder; 